import { GetServerSidePropsContext } from 'next';
import { RefObject } from 'react';

import { StatusError } from '~/lib/fetch';
import { SessionObjectProps } from '~/types/user';
import { DEFAULT_PLUS_DISCOUNT_PERCENTAGE, EMAIL_REGEXP } from './constants';

interface Cookie {
  key: string;
  value: string;
}

export const isValidEmail = (email: string): boolean =>
  EMAIL_REGEXP.test(String(email).toLowerCase());

export function removeHTMLTags(str: string): string {
  let cleanedString = '';

  if (str === null || str === '') return '';
  else cleanedString = str.toString();

  // Regex to replace the identified HTML tag with an empty string.
  return cleanedString.replace(/(<([^>]+)>)/gi, '');
}

export const getCSRFCookie = (): string => {
  if (!process.browser) {
    return '';
  }

  const cookie = document.cookie
    .split(';')
    .map(
      (c): Cookie => ({
        key: c.split('=')?.[0]?.trim(),
        value: c.split('=')?.[1]?.trim(),
      }),
    )
    .find((c): boolean => c.key === 'csrftoken');

  return cookie ? cookie.value : '';
};

/**
 * @param text The HTML string to parse
 * @param maxLength The maximum length of the returned string
 * @param minLength The minimum length of the returned string
 *
 * @returns The first sentence of the given text
 */
export const firstSentence = ({
  text,
  maxLength = 60,
  minLength = 8,
  showEllipsis = false,
}: {
  text: string;
  maxLength?: number;
  minLength?: number;
  showEllipsis?: boolean;
}): string => {
  if (text.length <= minLength) {
    return text;
  }

  // find first sentence with a minimum of minLength chars
  for (
    let i = Math.min(text.length, minLength);
    i < Math.min(text.length, maxLength);
    i += 1
  ) {
    const c = text.charAt(i);

    if (['?', '.', '!'].includes(c)) {
      return text.substr(0, i + 1);
    }
  }

  // no separator found, return full string
  if (text.length < maxLength) {
    return text;
  }

  // no sentence found, stop at last word prior to maxLength
  const i = text.lastIndexOf(' ', maxLength);
  if (i > 0) {
    return text.substr(0, i) + (showEllipsis ? '...' : '');
  }

  // last resort just trim maxLength
  return text.substr(0, maxLength) + (showEllipsis ? '...' : '');
};

/**
 * (Ported over from appsumo-2)
 * Find last ' ' within maxLength and return substring including last word (excluding the ' ')
 *
 * @param str The HTML string to parse
 * @param maxLength The maximum length of the returned string
 *
 * @returns substring of str with maxLength of 160
 */
export const lastWordAt = (str: string, maxLength: number = 160) => {
  if (str.length > maxLength) {
    const i = str.lastIndexOf(' ', maxLength);
    if (i > 0) {
      return str.substr(0, i);
    }
  }

  return str.substr(0, maxLength);
};

/**
 * (port over from appsumo-2)
 * Ensure string is sentence case
 *
 * @param str The string to convert to sentence case
 *
 * @returns The string in sentence case
 */
export const sentenceCase = (str: string) => {
  const newString = str
    .toLowerCase()
    .replace(/(^\s*\w|[.!?]\s*\w)/g, (c) => c.toUpperCase());
  return newString;
};

/**
 * Get UTM params for social share modal.
 * @param {Object} data - Deal or wishlist data
 * @param {String} data.slug - Deal or wishlist slug
 * @param {Number} data.id - Deal or wishlist id
 * @param {Number} userId - Id of the logged in user
 * @returns {Object} An object with the social share utm params
 */
export const getUtmParamsForSocialShare = ({
  data,
  userId,
}: {
  data: { slug: string; id: number };
  userId: number | undefined;
}) => {
  const utmMedium = 'utm_medium=social';
  const getUtmSource = (social: string) =>
    `utm_source=${data.slug}-share-${social}`;
  const getUtmCampaign = (social: string) => {
    const userIdString = userId ? `-${userId}` : '';
    return `utm_campaign=${data.slug}-share-${social}-${data.id}${userIdString}`;
  };

  return {
    text: `?${getUtmSource('link')}&${utmMedium}&${getUtmCampaign('link')}`,
    email: `?${getUtmSource('email')}&${utmMedium}&${getUtmCampaign('email')}`,
    facebook: `?${getUtmSource('facebook')}&${utmMedium}&${getUtmCampaign(
      'facebook',
    )}`,
    linkedin: `?${getUtmSource('linkedin')}&${utmMedium}&${getUtmCampaign(
      'linkedin',
    )}`,
    twitter: `?${getUtmSource('twitter')}&${utmMedium}&${getUtmCampaign(
      'twitter',
    )}`,
  };
};

/**
 * Open a new window with the given url and parameters
 * @param {String} url - The url to open
 * @param {String} windowName - The name of the window
 * @param {Boolean} popup - Whether to open the window as a popup
 * @param {Number} height - The height of the window
 * @param {Number} width - The width of the window
 * @returns {void}
 */
export const openExternalWindow = ({
  url,
  windowName,
  popup,
  height = 600,
  width = 600,
}: {
  url: string;
  windowName: string;
  popup: boolean;
  height?: number;
  width?: number;
}): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const left = (window.innerWidth - width) / 2;
  const top = (window.innerHeight - height) / 2;
  const windowFeatures = [
    `left=${left}`,
    `top=${top}`,
    `width=${width}`,
    `height=${height}`,
    `popup=${popup}`,
  ].join(',');
  window.open(url, windowName, windowFeatures);
};
// Useful for hidden fields with string values
export const isTrue = (value: string) => value.toLowerCase() === 'true';
export const isFalse = (value: string) => value.toLowerCase() === 'false';
export const getPlusDiscount = (session?: SessionObjectProps) =>
  `${session?.plus_percentage_off ?? DEFAULT_PLUS_DISCOUNT_PERCENTAGE}`;

export const getCSSRGBValues = (rgb: string): string[] =>
  rgb.replace(/[^\d,]/g, '').split(',');

const componentToHex = (value: number | string) => {
  const hex = Number(value).toString(16);
  return hex.length == 1 ? '0' + hex : hex;
};

export const rgbToHex = (
  red: string | number,
  green: string | number,
  blue: string | number,
): string => {
  return `#${componentToHex(red)}${componentToHex(green)}${componentToHex(
    blue,
  )}`;
};

/**
 * Make given color darker or lighter by given magnitude
 * @param {String} hexColor - The color to make darker or lighter
 * @param {Number} percent - The percent to make the color darker or lighter (Positive for light, negative for dark)
 * @returns {String} The darker or lighter color
 *
 * More info
 * https://css-tricks.com/snippets/javascript/lighten-darken-color/
 * https://gist.github.com/renancouto/4675192?permalink_comment_id=2976324#gistcomment-2976324
 */
export const colorNewShade = (hexColor: string, percent: number): string => {
  const num = parseInt(hexColor.replace('#', ''), 16);
  const magnitude = Math.round(2.55 * percent);

  let red = (num >> 16) + magnitude;
  if (red > 255) red = 255;
  if (red < 0) red = 0;

  let blue = ((num >> 8) & 0x00ff) + magnitude;
  if (blue > 255) blue = 255;
  if (blue < 0) blue = 0;

  let green = (num & 0x0000ff) + magnitude;
  if (green > 255) green = 255;
  if (green < 0) green = 0;

  return (
    '#' +
    (0x1000000 + red * 0x10000 + blue * 0x100 + green).toString(16).slice(1)
  );
};

const getRGB = (value: string): number => parseInt(value, 16) || Number(value);

function getsRGB(value: string): number {
  return getRGB(value) / 255 <= 0.03928
    ? getRGB(value) / 255 / 12.92
    : Math.pow((getRGB(value) / 255 + 0.055) / 1.055, 2.4);
}

function getLuminance(hexColor: string): number {
  return (
    0.2126 * getsRGB(hexColor.substring(1, 3)) +
    0.7152 * getsRGB(hexColor.substring(3, 5)) +
    0.0722 * getsRGB(hexColor.substring(hexColor.length - 2))
  );
}

function getContrast(firstColor: string, secondColor: string): number {
  const L1 = getLuminance(firstColor);
  const L2 = getLuminance(secondColor);

  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
}

/**
 * Get the tailwind text color class contrast based on the given background color
 * @param {String} backgroundHexColor - The background color
 * @returns {String} The contrast text color
 *
 * More info: https://wunnle.com/dynamic-text-color-based-on-background
 */
export const getTailwindContrastTextClass = (
  backgroundHexColor: string,
): string => {
  const whiteContrast = getContrast(backgroundHexColor, '#ffffff');
  const blackContrast = getContrast(backgroundHexColor, '#000000');

  return whiteContrast > blackContrast ? 'text-white' : 'text-black-pearl';
};

export const getCookieString = (
  req: GetServerSidePropsContext['req'],
): string => {
  if (!req || !req.cookies) {
    return '';
  }
  const cookieString = Object.keys(req.cookies)
    .map((key) => `${key}=${req.cookies[key]}`)
    .join('; ');
  return cookieString;
};

/**
 * Get the hash value from the given path.
 * @param {String} path - The path to get the hash value from
 * @returns {String} The hash value
 */
export const getHashFromPath = (path: string) => {
  const parts = path.split('?');
  const hashPart = parts[0].split('#');

  return hashPart[1] || '';
};

/**
 * Get the query parameters from the given path value.
 * @param {String} path - The path value to get the query parameters from
 * @returns {URLSearchParams} The query parameters
 */
export const getQueryFromPath = (path: string): URLSearchParams => {
  const queryString = path.split('?')[1];

  return new URLSearchParams(queryString);
};

/**
 * Generate range of numbers from min to max. Fill in missing numbers.
 * @param {Number} min - The minimum number
 * @param {Number} max - The maximum number
 * @returns {Array<Number>} The range of numbers
 */
export function range(min: number, max: number): Array<number> {
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
}

/**
 * Format date to human readable format. For example, 2021-01-01 to January 1, 2021
 *
 * @param {String} inputDate - The date to format
 * @param {Object} options - The options to pass to the toLocaleDateString function
 * @returns {String} The formatted date
 */
export function formatToHumanReadableDate(
  inputDate: string,
  options?: Intl.DateTimeFormatOptions,
) {
  const locale =
    typeof navigator !== 'undefined' ? navigator.language : 'en-US';
  const formattedDate = new Date(inputDate)?.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  });

  return formattedDate;
}

/**
 * Format date to human readable format. For example, 2025-01-03T16:32:06Z to 01/03/2025
 *
 * @param {String} inputDate - The date to format
 * @returns {String} The formatted date
 */
export function formatStandardCalendarDate(inputDate: string) {
  if (!inputDate) return;

  const locale =
    typeof navigator !== 'undefined' ? navigator.language : 'en-US';
  const formattedDate = new Date(inputDate)?.toLocaleDateString(locale, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

  return formattedDate;
}

export const manuallyClickRef = (ref: RefObject<HTMLInputElement>) => {
  if (ref.current) {
    ref.current.click();
  }
};

export const setFormErrorsFromResponse = async (
  formEl: any,
  error: StatusError,
) => {
  if (error.response?.status === 400) {
    const errors = await error.response.json();

    Object.entries(errors).forEach(([field, error]) => {
      formEl.current?.setError(field, {
        type: 'manual',
        message: error,
      });
    });
  }
};

export const isExperimentalVariant = (variant?: string) => {
  if (!variant) return false;
  return !['control', 'disabled'].includes(variant);
};
